'use client';

import type { ReactNode } from 'react';

import { useState } from 'react';

import Box from '@mui/material/Box';

import { LoadingImg } from './loading-img';

/**
 * 실물 한 컷을 칸에 까는 **한 곳** — 그림을 **자르지 않고 통째로** 보여 준다.
 *
 * 🔑 소셜 실물은 세로(9:16)·가로(16:9)·정사각이 섞여 온다. 칸에 맞춰 잘라 채우면(`cover`)
 *   세로 영상은 **얼굴이 잘려** 누구인지 안 보이고, 가로 영상은 가운데만 남는다. 광고 소재는
 *   자막·가격이 잘려 무엇을 파는지 못 읽는다. 그래서 **`contain`** 으로 통째로 넣고,
 *   남는 자리는 **같은 그림을 흐리게 깐 배경**으로 메운다.
 * ⚠️ 흐린 배경은 장식이 아니라 **여백을 메우는 것**이다 — 회색 빈 띠를 두면 격자가 성기게 보인다.
 * ⚠️ 칸의 크기는 부르는 쪽이 정한다(`height` 또는 `ratio`) — 그림이 칸을 밀지 않는다.
 * 🔑 받는 동안은 칸에 옅은 반짝임이 깔리고(`LoadingImg`), 깨진 그림은 빈 칸으로 남는다.
 * ⛔ 칸마다 `backgroundSize` 를 직접 쓰지 마라 — 한 화면 안에서 어떤 카드는 잘리고 어떤 카드는
 *   안 잘리면 같은 격자로 안 읽힌다.
 *
 * ⚠️ 줄줄이 흐르는 **격자**(소재 찾기·허브의 실물 격자)는 이 부품이 아니라 `MediaGrid` 가 그린다 —
 *   거기서는 칸 높이 자체를 그림 비율로 맞춰(justified) 여백이 아예 안 생긴다.
 *
 * ## `fill` — 칸을 **꽉 채운다**(잘라서)
 * 칸 크기는 부르는 쪽이 정하고 그림이 **잘려서** 칸을 다 메운다. 남는 자리가 없으니 회색 칸도
 * 흐린 배경도 없다.
 * 🔑 **작은 맛보기**(목록 줄의 40~60px 정사각)가 그 자리다 — 거기서는 「무슨 결의 그림인가」만
 *   알면 되고, 비율을 지키느라 두른 회색 띠가 그림보다 먼저 보인다.
 * ⛔ **큰 카드에는 쓰지 마라** — 세로 영상은 얼굴이, 광고 소재는 자막·가격이 잘려 무엇인지 못 읽는다
 *   (그게 이 부품이 기본을 `contain` 으로 두는 까닭이다).
 */
/**
 * 작은 맛보기 컷의 **한 변**(정사각) — 목록 줄·카드 안에 까는 `fill` 컷이 이 크기로 선다.
 *
 * 🔑 화면이 달라도 **같은 크기**라야 같은 맛보기로 읽힌다 — 브랜드 허브의 줄과 크리에이터 허브의
 *   줄을 나란히 놓고 보는 자리가 있다.
 * 🚨 부르는 쪽의 칸 폭 ≥ `컷 수 × SHOT_PREVIEW + 사이 여백` — 모자라면 마지막 컷이 잘린다.
 *   여기를 키우면 그 칸들도 같이 넓혀야 한다.
 */
export const SHOT_PREVIEW = 52;

export function MediaShot({
  src,
  height,
  width,
  ratio,
  radius = 1,
  blur = true,
  fill = false,
  children,
}: {
  /** 빈 값이면 빈 칸만 남는다 — 앱에서 대신 그림을 만들지 않는다 */
  src: string;
  /** 칸의 높이 — 부르는 쪽 격자가 정한다 */
  height?: number | string;
  /** 안 주면 남는 자리를 다 채운다(격자 한 칸) */
  width?: number | string;
  /** 높이 대신 비율로 잡을 때 — `'4 / 5'` 같은 CSS 값 */
  ratio?: string;
  radius?: number;
  /** 뒤에 깔 흐린 배경 — 끄면 남는 자리가 빈 회색이 된다 */
  blur?: boolean;
  /**
   * 칸을 **잘라서 꽉 채운다**(머리말 `fill`) — 작은 맛보기 전용이다.
   * ⛔ 큰 카드에는 쓰지 마라 — 얼굴·자막이 잘린다.
   */
  fill?: boolean;
  /** 실물 위에 얹을 것(재생 표시·배지) */
  children?: ReactNode;
}) {
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const shown = src && brokenSrc !== src ? src : '';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        borderRadius: radius,
        bgcolor: 'background.neutral',
        ...(height != null ? { height } : null),
        ...(ratio ? { aspectRatio: ratio } : null),
        ...(width != null ? { width } : { flex: 1, minWidth: 0 }),
      }}
    >
      {shown && blur && !fill && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${shown}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            // 가장자리까지 색이 차도록 키워서 흐린다 — 안 키우면 테두리에 맨 그림이 비친다
            filter: 'blur(14px)',
            transform: 'scale(1.2)',
            opacity: 0.45,
          }}
        />
      )}
      {shown && (
        <LoadingImg
          src={shown}
          loading="lazy"
          draggable={false}
          onError={() => setBrokenSrc(shown)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: 1,
            height: 1,
            display: 'block',
            // 🔑 기본은 자르지 않는다 — 원래 비율 그대로 칸 안에 다 들어간다. `fill` 만 잘라 채운다
            objectFit: fill ? 'cover' : 'contain',
            objectPosition: 'center',
          }}
        />
      )}
      {children}
    </Box>
  );
}
